import { ISortedPlanificationActivity } from '@sdk/contracts';
import { dateProvider } from '@utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CardWrapper,
  ContentWrapper,
  DateWrapper,
  WeekDay,
  DayNumber
} from './CarouselItem.styles';

import { Icon } from '@/components/Icon/Icon';
import { isActivityCompleted } from '@/utils';

type Props = {
  date: string;
  onClick: (date: string) => void;
  activities?: ISortedPlanificationActivity[];
  isSelected: boolean;
};

export const CarouselItem: React.FC<Props> = ({ date, onClick, activities, isSelected }) => {
  const { t } = useTranslation('StudentDashboardTranslations');

  const allActivitiesCompleted = useMemo(
    () => Boolean(activities?.every(a => isActivityCompleted(a.activity?._id, a.subscription))),
    [activities]
  );

  const isRestDay = !activities || !activities.length;

  const getWeekdayTranslationKey = (dateString: string) => {
    const dateObj = dateProvider.parse(dateString, 'yyyy-MM-dd');
    const dayOfWeek = dateObj.getDay();

    const weekdayKeys = [
      'WEEKDAY_SUN',
      'WEEKDAY_MON',
      'WEEKDAY_TUE',
      'WEEKDAY_WED',
      'WEEKDAY_THU',
      'WEEKDAY_FRI',
      'WEEKDAY_SAT'
    ] as const;

    return weekdayKeys[dayOfWeek];
  };

  const getDayName = () => {
    const today = dateProvider.format(new Date(), 'yyyy-MM-dd');
    const isToday = date === today;

    if (isToday) {
      return 'Hoy';
    }
    const weekdayKey = getWeekdayTranslationKey(date);
    return t(weekdayKey);
  };

  const getDayNumber = () => {
    const dateObj = dateProvider.parse(date, 'yyyy-MM-dd');
    return dateObj.getDate().toString();
  };

  const handleClick = () => onClick(date);

  const getIcon = () => {
    if (isRestDay) return <Icon i="CircleSolid" color="black6" />;
    const today = dateProvider.format(new Date(), 'yyyy-MM-dd');
    const isPastDay = date < today;
    if (!allActivitiesCompleted && isPastDay) return <Icon i="CloseFilled" color="black6" />;
    if (allActivitiesCompleted) return <Icon i="CheckmarkFilled" color="green6" />;
    return <Icon i="CircleDash" color={isSelected ? 'green6' : 'black6'} />;
  };

  return (
    <CardWrapper
      $isrest={isRestDay}
      $isselected={isSelected}
      $istoday={date === dateProvider.format(new Date(), 'yyyy-MM-dd')}
      onClick={() => handleClick()}>
      <ContentWrapper>
        <DateWrapper>
          <WeekDay>{getDayName()}</WeekDay>
          <DayNumber>{getDayNumber()}</DayNumber>
        </DateWrapper>
        {getIcon()}
      </ContentWrapper>
    </CardWrapper>
  );
};
